import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Alert } from 'rsuite';
import { AlertWithTimestamp } from '../../../../../../components/AlertWithTimestamp';
import { extractApiErrorMessage } from '../../../../../../utils/extractApiErrorMessage';
import { CampaignPromotionsType, CampaignTypeWithIndex, campaignPromotionsformSchema } from '../lib/schema';
import { CampaignTypeResponse } from '../lib/types';
import {
  CampaignConfig,
  changeCampaignTypeOrder,
  createCampaignType,
  deleteCampaignType,
  getCampaignSetup,
  getCampaignTypes,
  updateCampaignType,
  upsertCampaignSetup,
} from '../services/campaign-service';

export function useCampaign() {
  const form = useForm<CampaignPromotionsType>({
    resolver: zodResolver(campaignPromotionsformSchema),
    defaultValues: {
      campaignTypes: [],
      campaignPrioritization: 'PRIORITY_TYPE',
      campaignOfferEditing: 'ALLOW_EDITING',
    },
  });

  const [openUpsert, setOpenUpsert] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CampaignTypeWithIndex | null>(null);
  const [openDelete, setOpenDelete] = useState(false);

  const campaignFields = useFieldArray({
    control: form.control,
    name: 'campaignTypes',
  });

  const { data: campaignConfig } = useQuery<CampaignConfig>({
    queryKey: ['campaign-config'],
    queryFn: getCampaignSetup,
  });

  const {
    data: campaignTypes,
    refetch,
    isLoading,
    isRefetching,
  } = useQuery<CampaignTypeResponse[]>({
    queryKey: ['campaign-types'],
    queryFn: getCampaignTypes,
  });

  const { mutate: mutateCampaignConfig } = useMutation({
    mutationFn: upsertCampaignSetup,
    onSuccess: () => {
      Alert.success('Configurações de campanhas salvas com sucesso');
    },
  });

  const { mutate: mutateCampaignTypeCreate } = useMutation({
    mutationFn: createCampaignType,
    onSuccess: () => {
      Alert.success('Tipo de campanha criado com sucesso');
      refetch();
    },
  });

  const { mutate: mutateCampaignTypeDelete } = useMutation({
    mutationFn: deleteCampaignType,
    onSuccess: () => {
      Alert.success('Tipo de campanha excluído com sucesso');
      refetch();
    },
    onError: error => {
      const apiErrorMessage = extractApiErrorMessage(error);
      const message = apiErrorMessage || 'Erro ao excluir campanha!';
      AlertWithTimestamp({ message });
    },
  });

  const { mutate: mutateCampaignTypeUpdate } = useMutation({
    mutationFn: updateCampaignType,
    onSuccess: () => {
      Alert.success('Tipo de campanha atualizado com sucesso');
      refetch();
    },
  });

  const { mutate: mutateCampaignTypeOrder, isLoading: isOrderLoading } = useMutation({
    mutationFn: changeCampaignTypeOrder,
    onSuccess: () => {
      Alert.success('Tipo de campanha ordenado com sucesso');
    },
    onError: error => {
      const apiErrorMessage = extractApiErrorMessage(error);
      const message = apiErrorMessage || 'Erro ao ordenar campanha!';
      AlertWithTimestamp({ message });
      refetch();
    },
  });

  const handleSubmitConfig = (data: CampaignPromotionsType) => {
    const campaignConfig = {
      priority: data.campaignPrioritization,
      editOffer: data.campaignOfferEditing === 'ALLOW_EDITING',
      campaignTypes: data.campaignTypes,
    };
    mutateCampaignConfig(campaignConfig);
  };

  const handleOpenCreate = () => {
    setSelectedItem(null);
    setOpenUpsert(true);
  };

  const handleOpenEdit = (item: CampaignTypeWithIndex) => {
    setSelectedItem(item);
    setOpenUpsert(true);
  };

  const handleSaveCampaignType = async (campaignType: Omit<CampaignTypeWithIndex, 'order'>) => {
    setOpenUpsert(false);

    if (campaignType.typeId) {
      mutateCampaignTypeUpdate({ id: campaignType.typeId, name: campaignType.campaignType });
      return;
    }

    mutateCampaignTypeCreate(campaignType.campaignType);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;

    const currentItem = campaignFields.fields[index];
    const previousItem = campaignFields.fields[index - 1];

    campaignFields.update(index - 1, {
      ...currentItem,
      order: index,
    });

    campaignFields.update(index, {
      ...previousItem,
      order: index + 1,
    });

    mutateCampaignTypeOrder({ firstId: currentItem.typeId!, secondId: previousItem.typeId! });
  };

  const moveDown = (index: number) => {
    if (index === campaignFields.fields.length - 1) return;

    const currentItem = campaignFields.fields[index];
    const nextItem = campaignFields.fields[index + 1];

    campaignFields.update(index, {
      ...nextItem,
      order: index + 1,
    });

    campaignFields.update(index + 1, {
      ...currentItem,
      order: index + 2,
    });

    mutateCampaignTypeOrder({ firstId: nextItem.typeId!, secondId: currentItem.typeId! });
  };

  const handleOpenDelete = (item: CampaignTypeWithIndex) => {
    setSelectedItem(item);
    setOpenDelete(true);
  };

  const existsCampaignWithSameName = (campaignTypeName: string, typeId: string | null) => {
    if (!campaignTypes?.length || !campaignTypeName) return false;

    const nameToCompare = campaignTypeName.trim().toLowerCase();

    if (!typeId) {
      return !!campaignTypes.some(c => c.name.trim().toLowerCase() === nameToCompare);
    }

    return !!campaignTypes.some(c => c.id !== typeId && c.name.trim().toLowerCase() === nameToCompare);
  };

  useEffect(() => {
    if (campaignConfig) {
      form.setValue('campaignPrioritization', campaignConfig.priority);
      form.setValue('campaignOfferEditing', campaignConfig.editOffer ? 'ALLOW_EDITING' : 'DO_NOT_ALLOW_EDITING');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignFields.fields.length, campaignConfig]);

  useEffect(() => {
    const hasCampaignTypes = !!campaignTypes?.length;
    if (hasCampaignTypes) {
      campaignFields.replace(
        campaignTypes?.map(c => {
          return {
            campaignType: c.name,
            order: c.order,
            typeId: c.id,
            campaignsName: c.campaignsName ?? [],
          };
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignTypes]);

  return {
    form,
    campaignFields,
    state: {
      openUpsert,
      selectedItem,
      openDelete,
      isLoadingCampaignTypes: isLoading || isRefetching,
      isOrderLoading,
    },
    handlers: {
      setOpenUpsert,
      setSelectedItem,
      setOpenDelete,
      handleOpenDelete,
      handleSubmitConfig,
      handleOpenCreate,
      handleOpenEdit,
      handleSaveCampaignType,
      moveUp,
      moveDown,
      mutateCampaignTypeDelete,
      existsCampaignWithSameName,
    },
  };
}
